import React, { useEffect, useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';

import { OnboardingButton } from '../Components/OnboardingButton';
import { colors, fontStyles, spacing } from '../../../styles';
import SafeArea from '../../SafeArea';
import { trackOnboardingCompletion } from '../../../utils/analytics';
import * as stores from '../../../stores';
import { completeOnboarding } from '../../../utils/onboarding';
import { setInvestorPersona } from '../../MorePage/InvestorPersonaSelector';
import { InvestorType } from '../../../utils/smartChatPrompts';

// Map investor type to display label
const investorTypeLabels: Record<InvestorType, string> = {
  'new-investor': 'Learning basics',
  'active-trader': 'Active trading',
  researcher: 'Deep research',
};

export const PurchaseResult: React.FC = () => {
  const history = useHistory();
  const [isSubscriber, setIsSubscriber] = useState(false);

  // Get configured values from localStorage
  const configuredData = useMemo(() => {
    const motivations: InvestorType[] = JSON.parse(localStorage.getItem('onboardingMotivations') || '[]');
    const watchlist = stores.General.getRawState().watchlist;
    const selectedSymbols = Object.keys(watchlist).filter((symbol) => watchlist[symbol]);

    // Get enabled alerts - preferences are stored as array of {id, description, enabled}
    const notificationPrefs = JSON.parse(localStorage.getItem('notificationPreferences') || '[]');
    const enabledAlerts: string[] = [];
    if (Array.isArray(notificationPrefs)) {
      const alertLabels: Record<string, string> = {
        smart_notifications: 'Daily',
        news_alerts: 'News',
        daily_alerts: 'Price',
        earnings_alerts: 'Earnings',
      };
      notificationPrefs.forEach((pref: { id: string; enabled: boolean }) => {
        if (pref.enabled && alertLabels[pref.id]) {
          enabledAlerts.push(alertLabels[pref.id]);
        }
      });
    }

    return {
      investorType: motivations[0] ? investorTypeLabels[motivations[0]] : null,
      symbols: selectedSymbols,
      alerts: enabledAlerts,
    };
  }, []);

  useEffect(() => {
    // Check if user subscribed during this session
    const hasSubscribed = localStorage.getItem('hasSubscribed') === 'true';
    setIsSubscriber(hasSubscribed);
    // Clean up the flag after reading to prevent it from persisting
    localStorage.removeItem('hasSubscribed');
  }, []);

  const handleFinish = () => {
    // Check if there's a previous route to return to
    const previousRoute = localStorage.getItem('previousRoute');
    const wasAlreadyCompleted = localStorage.getItem('wasOnboardingCompleted') === 'true';

    completeOnboardingAndCleanup();

    if (wasAlreadyCompleted && previousRoute) {
      localStorage.removeItem('previousRoute');
      history.push(previousRoute);
    } else {
      // Redirect new users to chat (Ask AI about my stocks)
      history.push('/chat');
    }
  };

  // Navigate to watchlist
  const handleViewWatchlist = () => {
    completeOnboardingAndCleanup();
    history.push('/portfolios');
  };

  // Navigate to Chat
  const handleExploreIdeas = () => {
    completeOnboardingAndCleanup();
    history.push('/chat');
  };

  // Helper to complete onboarding and clean up
  const completeOnboardingAndCleanup = () => {
    let wasAlreadyCompleted = false;
    try {
      wasAlreadyCompleted = localStorage.getItem('wasOnboardingCompleted') === 'true';
    } catch (e) {
      console.warn('Error accessing localStorage:', e);
    }

    completeOnboarding();

    if (!wasAlreadyCompleted) {
      const watchlist = stores.General.getRawState().watchlist;
      const selectedSymbols = Object.keys(watchlist).filter((symbol) => watchlist[symbol]);
      const motivations = JSON.parse(localStorage.getItem('onboardingMotivations') || '[]');
      const startTime = parseInt(localStorage.getItem('onboardingStartTime') || '0', 10);
      const completionTime = startTime > 0 ? Date.now() - startTime : 0;
      const parsedTotalSteps = parseInt(localStorage.getItem('onboardingCompletionTotalSteps') || '', 10);
      const totalSteps = Number.isNaN(parsedTotalSteps) ? 6 : parsedTotalSteps;

      trackOnboardingCompletion({
        totalSteps,
        selectedMotivations: motivations,
        selectedSymbols,
        completionTime,
      });

      if (motivations.length > 0) {
        setInvestorPersona(motivations[0]);
      }

      localStorage.removeItem('onboardingMotivations');
      localStorage.removeItem('onboardingStartTime');
    }

    localStorage.removeItem('wasOnboardingCompleted');
    localStorage.removeItem('onboardingCompletionTotalSteps');
  };

  return (
    <SafeArea>
      <div
        css={css`
          width: 100%;
          max-width: 1024px;
          margin: 0 auto;
          padding: ${spacing.deci};
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            padding-top: 8%;
            gap: ${spacing.deci};
          `}
        >
          {/* Success Icon */}
          <div
            css={css`
              font-size: 80px;
              line-height: 1;
            `}
          >
            🎉
          </div>

          {/* Title */}
          <h1
            css={css`
              ${fontStyles.shark};
              color: ${colors.liquorice};
              font-size: 28px;
              text-align: center;
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {isSubscriber ? 'Subscription activated!' : "You're all set!"}
          </h1>

          {/* Subtitle */}
          <p
            css={css`
              ${fontStyles.dog};
              color: ${colors.silver};
              font-size: 15px;
              text-align: center;
              html[data-theme='dark'] & {
                color: ${colors.cloud};
              }
            `}
          >
            Here&apos;s what we configured for you:
          </p>

          {/* Configuration Summary */}
          <div
            css={css`
              width: 100%;
              max-width: 320px;
              background: ${colors.fog};
              border-radius: 12px;
              padding: ${spacing.deci};
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
              html[data-theme='dark'] & {
                background: ${colors.tungsten};
              }
            `}
          >
            {[
              {
                label: 'Investor profile',
                value: configuredData.investorType,
              },
              {
                label: 'Watchlist',
                value: configuredData.symbols.length > 0 ? configuredData.symbols.join(', ') : null,
              },
              {
                label: 'Alerts',
                value: configuredData.alerts.length > 0 ? configuredData.alerts.join(' + ') : null,
                isLast: true,
              },
            ].map((row) => (
              <div
                key={row.label}
                css={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  padding: ${spacing.centi} 0;
                  ${!row.isLast && `border-bottom: 0.5px solid ${colors.cloud};`}
                  html[data-theme='dark'] & {
                    border-bottom-color: ${colors.liquorice};
                  }
                `}
              >
                <span
                  css={css`
                    ${fontStyles.cat};
                    color: ${colors.liquorice};
                    min-width: 100px;
                    flex-shrink: 0;
                    html[data-theme='dark'] & {
                      color: ${colors.sugar};
                    }
                  `}
                >
                  {row.label}
                </span>
                <span
                  css={css`
                    ${fontStyles.cat};
                    color: ${row.value ? colors.leaf : colors.silver};
                    text-align: right;
                    flex: 1;
                  `}
                >
                  {row.value || 'Not set'}
                </span>
                {row.value && (
                  <span
                    css={css`
                      ${fontStyles.cat};
                      color: ${colors.leaf};
                      margin-left: ${spacing.milli};
                    `}
                  >
                    ✓
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* What would you like to do first? */}
          <p
            css={css`
              ${fontStyles.dog};
              color: ${colors.silver};
              font-size: 14px;
              text-align: center;
              margin-top: ${spacing.deci};
              margin-bottom: ${spacing.centi};
              html[data-theme='dark'] & {
                color: ${colors.cloud};
              }
            `}
          >
            What would you like to do first?
          </p>
        </div>

        {/* Bottom CTAs */}
        <div
          css={css`
            width: 100%;
            padding: ${spacing.deci} 0;
          `}
        >
          {/* Primary CTA */}
          <OnboardingButton onClick={handleFinish}>Ask AI about my stocks</OnboardingButton>

          {/* Secondary CTAs */}
          <div
            css={css`
              display: flex;
              justify-content: center;
              gap: ${spacing.deci};
              margin-top: ${spacing.deci};
            `}
          >
            <button
              onClick={handleViewWatchlist}
              css={css`
                background: none;
                border: none;
                color: ${colors.petrolBlue};
                ${fontStyles.cat};
                cursor: pointer;
                padding: ${spacing.centi};

                &:hover {
                  text-decoration: underline;
                }

                html[data-theme='dark'] & {
                  color: ${colors.skyBlue};
                }
              `}
            >
              View watchlist
            </button>
            <span
              css={css`
                color: ${colors.cloud};
                display: flex;
                align-items: center;
              `}
            >
              |
            </span>
            <button
              onClick={handleExploreIdeas}
              css={css`
                background: none;
                border: none;
                color: ${colors.petrolBlue};
                ${fontStyles.cat};
                cursor: pointer;
                padding: ${spacing.centi};

                &:hover {
                  text-decoration: underline;
                }

                html[data-theme='dark'] & {
                  color: ${colors.skyBlue};
                }
              `}
            >
              Explore Ideas
            </button>
          </div>
        </div>
      </div>
    </SafeArea>
  );
};
